import React, { useMemo, useState } from "react";
import { Select, Table, Tag, Tooltip } from "antd";
import type { ColumnsType } from "antd/es/table";
import moment from "moment";

import CustomPagination from "../../../../../../core/common/customPagination/CustomPagination";
import PredefinedDateRanges from "../../../../../../core/common/datePicker";
import { humanReadableDate } from "../../../../../../helper/dateTimeHelper";
import { formatDayPart } from "../utils/leaveHelpers";

interface LeaveHistorySectionProps {
  leaveHistory: any[];
  leaveTypes: any[];
  dateRange: { start: moment.Moment | null; end: moment.Moment | null };
  statusFilter: string;
  leaveTypeFilter: string;
  onDateChange: (start: moment.Moment, end: moment.Moment) => void;
  onResetDateRange: () => void;
  onLeaveTypeChange: (leaveTypeId: string) => void;
  onStatusChange: (status: string) => void;
  onClearFilters: () => void;
  onEditClick: (record: any) => void;
  onDeleteClick: (record: any) => void;
}

const STATUS_COLORS: Record<string, string> = {
  approved: "green",
  rejected: "red",
  pending: "gold",
  cancelled: "default",
};

const filterOption = (input: string, option?: { label: string; value: any }) =>
  (option?.label ?? "").toString().toLowerCase().includes(input.toLowerCase());

const LeaveHistorySection: React.FC<LeaveHistorySectionProps> = ({
  leaveHistory,
  leaveTypes,
  dateRange,
  statusFilter,
  leaveTypeFilter,
  onDateChange,
  onResetDateRange,
  onLeaveTypeChange,
  onStatusChange,
  onClearFilters,
  onEditClick,
  onDeleteClick,
}) => {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);

  const isCustomDateRange = useMemo(
    () =>
      !(
        dateRange?.start?.isSame(moment().startOf("month"), "day") &&
        dateRange?.end?.isSame(moment().endOf("month"), "day")
      ),
    [dateRange],
  );

  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (leaveTypeFilter) count++;
    if (statusFilter) count++;
    return count;
  }, [leaveTypeFilter, statusFilter]);

  const renderApprover = (_: string, record: any) => {
    const name = record.ApprovedBy || "";
    const image = record.ApprovedByImage;
    if (!name) return <span style={{ color: "#94a3b8" }}>—</span>;
    return (
      <div className="d-flex align-items-center">
        {image ? (
          <img
            src={image}
            alt={name}
            style={{
              width: 30,
              height: 30,
              borderRadius: "50%",
              objectFit: "cover",
              marginRight: 8,
            }}
          />
        ) : (
          <div
            style={{
              width: 30,
              height: 30,
              borderRadius: "50%",
              backgroundColor: "#1d71e3",
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 600,
              marginRight: 8,
              fontSize: 12,
            }}>
            {name.charAt(0).toUpperCase()}
          </div>
        )}
        <span style={{ fontSize: 13, color: "#0f172a" }}>{name}</span>
      </div>
    );
  };

  const renderStatus = (text: string, record: any) => {
    const key = (text || "").toLowerCase();
    const color = STATUS_COLORS[key] || "default";
    const label = text
      ? text.charAt(0).toUpperCase() + text.slice(1).toLowerCase()
      : "—";
    return (
      <span className="d-inline-flex align-items-center">
        <Tag color={color} style={{ fontSize: 11.5, margin: 0 }}>
          {label}
        </Tag>
        {key === "rejected" && record.rejectionReason && (
          <Tooltip title={`Rejection Reason: ${record.rejectionReason}`}>
            <i
              className="ti ti-info-circle ms-2"
              style={{ color: "#dc2626", fontSize: 14, cursor: "help" }}
            />
          </Tooltip>
        )}
      </span>
    );
  };

  const renderDateWithPart = (text: any, part: string) => (
    <div className="d-flex flex-column">
      <span style={{ fontSize: 13, fontWeight: 500, color: "#0f172a" }}>
        {humanReadableDate(text)}
      </span>
      <span
        className="bg-transparent-purple text-purple px-1"
        style={{ fontSize: 11, width: "fit-content", borderRadius: 4 }}>
        {formatDayPart(part)}
      </span>
    </div>
  );

  const columns: ColumnsType<any> = useMemo(
    () => [
      {
        title: "Sr No.",
        key: "srNo",
        width: 70,
        align: "center",
        fixed: "left",
        render: (_: any, __: any, index: number) => (
          <span style={{ fontSize: 12, fontWeight: 500, color: "#6b7280" }}>
            {(page - 1) * limit + index + 1}
          </span>
        ),
      },
      {
        title: "Leave Type",
        dataIndex: "LeaveType",
        key: "LeaveType",
        width: 170,
        fixed: "left",
        render: (text: string) => (
          <span style={{ fontSize: 13, fontWeight: 500, color: "#0f172a" }}>
            {text || "—"}
          </span>
        ),
        sorter: (a: any, b: any) =>
          (a.LeaveType || "").localeCompare(b.LeaveType || ""),
      },
      {
        title: "From",
        dataIndex: "From",
        key: "From",
        width: 140,
        render: (text: any, record: any) =>
          renderDateWithPart(text, record?.startDayPart),
      },
      {
        title: "To",
        dataIndex: "To",
        key: "To",
        width: 140,
        render: (text: any, record: any) =>
          renderDateWithPart(text, record?.endDayPart),
      },
      {
        title: "Days",
        dataIndex: "NoOfDays",
        key: "NoOfDays",
        width: 90,
        align: "center",
        render: (days: number) => (
          <Tag color="blue" style={{ fontSize: 11.5, margin: 0 }}>
            {days}
          </Tag>
        ),
      },
      {
        title: "Approved By",
        dataIndex: "ApprovedBy",
        key: "ApprovedBy",
        width: 200,
        render: renderApprover,
      },
      {
        title: "Applied On",
        dataIndex: "createdAt",
        key: "createdAt",
        width: 170,
        render: (date: any) => (
          <Tooltip title={humanReadableDate(date, true)}>
            <span
              className="d-inline-flex align-items-center"
              style={{
                fontSize: 11.5,
                fontWeight: 500,
                color: "#b45309",
                background: "#fff7ed",
                border: "1px solid #fed7aa",
                padding: "4px 8px",
                borderRadius: 6,
              }}>
              <i className="ti ti-calendar me-1" />
              {humanReadableDate(date, true)}
            </span>
          </Tooltip>
        ),
      },
      {
        title: "Status",
        dataIndex: "Status",
        key: "Status",
        width: 140,
        render: renderStatus,
      },
      {
        title: "Actions",
        key: "actions",
        width: 100,
        align: "center",
        fixed: "right",
        render: (_text: string, record: any) => {
          const status = (record.Status || "").toLowerCase();
          if (status === "approved" || status === "rejected") {
            return <span style={{ color: "#cbd5e1" }}>—</span>;
          }
          return (
            <div className="d-inline-flex align-items-center gap-1">
              <Tooltip title="Edit">
                <button
                  type="button"
                  className="btn btn-icon btn-sm btn-light"
                  style={{ borderRadius: 6 }}
                  onClick={() => onEditClick(record)}>
                  <i className="ti ti-edit" />
                </button>
              </Tooltip>
              <Tooltip title="Delete">
                <button
                  type="button"
                  className="btn btn-icon btn-sm btn-light"
                  style={{ borderRadius: 6 }}
                  onClick={() => onDeleteClick(record)}>
                  <i className="ti ti-trash" style={{ color: "#dc2626" }} />
                </button>
              </Tooltip>
            </div>
          );
        },
      },
    ],
    [page, limit, onEditClick, onDeleteClick],
  );

  const total = leaveHistory?.length ?? 0;
  const pagedRows = useMemo(
    () => (leaveHistory ?? []).slice((page - 1) * limit, page * limit),
    [leaveHistory, page, limit],
  );

  const leaveTypeOptions = useMemo(
    () =>
      (leaveTypes || []).map((t: any) => ({
        value: t.value.toString(),
        label: t.label,
      })),
    [leaveTypes],
  );

  return (
    <>
      <div className="card">
        <div className="card-header border-0 pb-0">
          <div className="d-flex align-items-center justify-content-between mb-2">
            <div>
              <h4
                className="mb-1 d-flex align-items-center"
                style={{ fontSize: 17, fontWeight: 600, color: "#0f172a" }}>
                <i
                  className="ti ti-history text-info me-2"
                  style={{ fontSize: 20 }}
                />
                Leave History
              </h4>
              <p className="text-muted mb-0" style={{ fontSize: 13 }}>
                View past applications — filter by leave type, status, or date
                range
              </p>
            </div>
          </div>
        </div>

        <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-2">
          <div className="d-flex gap-2 flex-wrap align-items-center">
            <div className="me-2">
              <PredefinedDateRanges
                onDateChange={onDateChange}
                startDate={dateRange?.start}
                endDate={dateRange?.end}
              />
            </div>
            {isCustomDateRange && (
              <button
                type="button"
                className="btn btn-outline-secondary d-inline-flex align-items-center mb-2"
                style={{ minHeight: 38 }}
                onClick={onResetDateRange}>
                <i className="ti ti-x me-1" />
                Clear Date
              </button>
            )}
          </div>

          <div className="d-flex my-xl-auto right-content align-items-center flex-wrap row-gap-2">
            <div className="me-2 mb-2">
              <Select
                showSearch
                filterOption={filterOption}
                style={{ width: 200, minHeight: 38 }}
                placeholder="Leave Type"
                value={leaveTypeFilter || undefined}
                allowClear
                onChange={(value) => onLeaveTypeChange(value || "")}
                options={leaveTypeOptions}
              />
            </div>
            <div className="me-2 mb-2">
              <Select
                style={{ width: 150, minHeight: 38 }}
                placeholder="Status"
                value={statusFilter || undefined}
                allowClear
                onChange={(value) => onStatusChange(value || "")}
                options={[
                  { value: "approved", label: "Approved" },
                  { value: "pending", label: "Pending" },
                  { value: "rejected", label: "Rejected" },
                ]}
              />
            </div>
            <div>
              <button
                type="button"
                className={`btn btn-md ${
                  activeFiltersCount > 0 ? "btn-warning" : "btn-outline-danger"
                } mb-2 position-relative py-2`}
                onClick={onClearFilters}
                disabled={activeFiltersCount === 0}>
                <i className="ti ti-refresh me-1" />
                Reset
                {activeFiltersCount > 0 && (
                  <span
                    className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger"
                    style={{ fontSize: "10px" }}>
                    {activeFiltersCount}
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="card-body p-0 mb-3 border-0">
        {total > 0 ? (
          <div
            className="bg-white"
            style={{
              borderRadius: 12,
              overflow: "hidden",
              border: "1px solid #e2e8f0",
            }}>
            <Table
              className="custom-data-table"
              columns={columns}
              dataSource={pagedRows}
              rowKey={(record: any, index?: number) =>
                record.id || `${record.From}-${index}`
              }
              size="middle"
              pagination={false}
              scroll={{ x: 1300 }}
              rowClassName={(_, index) =>
                index % 2 === 0 ? "data-table-row-light" : "data-table-row-dark"
              }
            />
            <CustomPagination
              currentPage={page}
              totalPages={Math.max(1, Math.ceil(total / limit))}
              total={total}
              limit={limit}
              hasNextPage={page * limit < total}
              hasPrevPage={page > 1}
              onPageChange={(p) => setPage(p)}
              onLimitChange={(l) => {
                setLimit(l);
                setPage(1);
              }}
            />
          </div>
        ) : (
          <div
            className="d-flex justify-content-center align-items-center bg-white"
            style={{
              minHeight: 320,
              borderRadius: 12,
              border: "1px solid #e2e8f0",
            }}>
            <div className="text-center">
              <i
                className="ti ti-folder-off"
                style={{ fontSize: 48, color: "#cbd5e1" }}
              />
              <h5 className="mt-3 mb-2 fw-semibold">No leave records</h5>
              <p className="text-muted mb-0">
                Try adjusting your filters or apply for a new leave.
              </p>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default LeaveHistorySection;
